import styled, { css } from 'styled-components';
import type { ColorName, StyledColorScheme } from 'styles/config/colors';
import themeColors, { baseColors } from 'styles/config/colors';
import themeSpacing from 'styles/config/spacing';

import { BodyStrong } from 'components/atoms/LegacyText';
import { LegacyTagVariant } from '.';

interface LegacyTagWrapperProps {
  append?: boolean;
  clickable?: boolean;
  $iconOnly?: boolean;
  variant: LegacyTagVariant;
  $hideBorder?: boolean;
}

const getBackgroundColourForTag = (
  colors: StyledColorScheme,
  variant: LegacyTagVariant,
) => {
  switch (variant) {
    case LegacyTagVariant.ACTIVE:
      return colors.scriptingNode;
    case LegacyTagVariant.ERROR:
      return colors.backgroundDanger;
    case LegacyTagVariant.SUCCESS:
      return colors.backgroundSuccess;
    case LegacyTagVariant.KEYWORD:
      return colors.backgroundInverse;
    case LegacyTagVariant.GRAY:
      return baseColors.grey[70];
    case LegacyTagVariant.INFO:
      return baseColors.blue['05'];
    case LegacyTagVariant.WARNING:
      return baseColors.yellow['05'];
  }
};

export const TagWrapper = styled.aside<LegacyTagWrapperProps>`
  display: inline-flex;
  gap: ${themeSpacing.xxs2};
  align-items: center;
  justify-content: flex-end;

  height: ${themeSpacing.sm3};
  padding: 0 ${themeSpacing.xs2};

  background-color: ${themeColors.backgroundPage};
  border: 1px solid ${themeColors.borderStrong};
  border-radius: ${themeSpacing.radiusXSmall};

  p {
    display: inline-block;
    width: max-content;
    font-size: ${themeSpacing.xs3};
    color: ${themeColors.textSecondary};
  }

  ${({ clickable }) =>
    clickable &&
    css`
      &:hover {
        cursor: pointer;
      }
    `}

  ${({ append }) =>
    append &&
    css`
      padding-right: 0;

      p {
        overflow: hidden;

        max-width: ${themeSpacing.xxl1};

        color: ${themeColors.textPrimary};
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `}
  ${({ variant }) =>
    variant === LegacyTagVariant.KEYWORD &&
    css`
      margin-right: 0.5rem;
    `}
  ${({ variant }) =>
    css`
      background-color: ${getBackgroundColourForTag(themeColors, variant)};
    `}
  ${({ variant }) =>
    [
      LegacyTagVariant.ERROR,
      LegacyTagVariant.SUCCESS,
      LegacyTagVariant.ACTIVE,
      LegacyTagVariant.KEYWORD,
      LegacyTagVariant.GRAY,
    ].includes(variant) &&
    css`
      p {
        color: ${themeColors.textPrimaryInverse};
      }
    `}
    ${({ variant }) =>
      variant === LegacyTagVariant.INFO &&
      css`
      gap: ${themeSpacing.xs1};
      padding: 0 ${themeSpacing.xs2};
      border-color: ${baseColors.blue['20']};

      p {
        color: ${themeColors.informationText01};
      }
    `}
    ${({ variant }) =>
      variant === LegacyTagVariant.WARNING &&
      css`
      gap: ${themeSpacing.xs1};
      padding: 0 ${themeSpacing.xs2};
      border-color: ${baseColors.yellow['20']};

      p {
        color: ${themeColors.textWarning};
      }
    `}
    
    ${({ $iconOnly }) =>
      $iconOnly &&
      css`
      padding: ${themeSpacing.xs1};
    `}

    ${({ $hideBorder }) =>
      $hideBorder &&
      css`
      border: none;
    `}
`;

export const TagText = styled(BodyStrong)<{ $color: ColorName }>`
  display: flex !important;
  align-items: center;

  && {
    color: ${({ $color, theme }) => theme.activeColorScheme.colors[$color]};
  }

  p {
    color: ${({ $color, theme }) => theme.activeColorScheme.colors[$color]};
  }
`;
